import React, { useEffect, useState } from "react";

import {
  Checkbox,
  Grid,
  InputWrapper,
  NumberInput,
  Text,
  TextInput,
  Select,
  Button,
  Tooltip,
} from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import { Clock, Plus, Trash, InfoCircle } from "tabler-icons-react";
import { toValidDate } from "../listings/hooks";

export interface DataEntryProps {
  label?: string;
  value?: any;
  edit?: boolean;
  inputProps?: any;
  dataType: string;
  form: any;
  fieldKey: string;
  selectOptions?: any[];
  selectLabelKey?: string;
  adjacentFields?: Record<string, string>;
  shouldIncludeTimeForDate?: boolean;
  decimalPrecision?: number;
}

export function DataEntry({
  label,
  value,
  edit,
  inputProps,
  dataType,
  form,
  fieldKey,
  selectOptions = [],
  selectLabelKey,
  adjacentFields,
  shouldIncludeTimeForDate = true,
  decimalPrecision = 2,
}: DataEntryProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputSize = isMobile ? "md" : "sm";
  const [date, setDate] = useState<Date | null>(null);
  const [time, setTime] = useState<Date | null>(null);

  useEffect(() => {
    if (dataType === "DATE" && edit) {
      let parsed = toValidDate(value);
      if (!parsed) {
        parsed = new Date();
      }
      setDate(parsed);
      setTime(parsed);
    }
  }, [edit, dataType, value]);

  const handleDateChange = (d: Date | null, t: Date | null) => {
    if (dataType === "DATE") {
      if (d) {
        setDate(d);
      }
      if (t) {
        setTime(t);
      }
    }
  };

  const handleArrayChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const existingValues = [...(form.values[fieldKey] || [])];
    existingValues[index] = event.target.value;
    form.setFieldValue(fieldKey, existingValues);
  };

  const handleArrayAdd = () => {
    form.setFieldValue(fieldKey, [...(form.values[fieldKey] || []), '']);
  };

  const handleArrayRemove = (index: number) => {
    const existingValues = [...(form.values[fieldKey] || [])];
    existingValues.splice(index, 1);

    form.setFieldValue(fieldKey, [...existingValues]);
  };

  useEffect(() => {
    if (date && time && !isNaN(date.getTime()) && !isNaN(time.getTime())) {
      const formattedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        time.getHours(),
        time.getMinutes()
      );
      if (!isNaN(formattedDate.getTime())) {
        const iso = formattedDate.toISOString();
        if (form.values[fieldKey] !== iso) {
          form.setFieldValue(fieldKey, iso);
        }
      }
    }
  }, [date, time, fieldKey, form]);
  return (
    <>
      <Grid columns={16} gutter={isMobile ? "xs" : 0}>
        <Grid.Col
          span={isMobile ? 16 : 8}
          style={{ textAlign: "start", alignContent: "center", marginBottom: isMobile ? 4 : 0 }}
        >
          <Text weight={500}>{label}</Text>
        </Grid.Col>
        <Grid.Col span={isMobile ? 16 : 8} style={{ textAlign: isMobile ? "start" : "end", alignContent: "center" }}>
          {!!edit
            ? (() => {
              switch (dataType) {
                case "NUMBER":
                  return (
                    <NumberInput
                      size={inputSize}
                      {...inputProps}
                      hideControls={true}
                      precision={decimalPrecision}
                      step={0.01}
                      style={{ width: "100%" }}
                    />
                  );
                case "STRING":
                  const isAddressField = fieldKey === "pickupAddress" || fieldKey === "deliveryAddress";
                  return (
                    <InputWrapper style={{ width: "100%" }}>
                      <TextInput
                        size={inputSize}
                        type="text"
                        placeholder={isAddressField ? "Enter address or paste Google/Apple maps link..." : inputProps?.placeholder}
                        rightSection={
                          isAddressField ? (
                            <Tooltip
                              label="Supports written addresses, WhatsApp pins, coordinates (lat,lng), or Google/Apple Maps links"
                              withArrow
                              wrapLines
                              width={220}
                            >
                              <InfoCircle size={18} style={{ display: 'block', opacity: 0.5, cursor: 'help' }} />
                            </Tooltip>
                          ) : undefined
                        }
                        style={{ width: "100%" }}
                        {...inputProps}
                      ></TextInput>
                    </InputWrapper>
                  );
                case "STRING_ARRAY":
                  return (
                    <>
                      {(value || []).map((item: string, i: number) => (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1em', gap: isMobile ? 8 : 0 }} key={i}>
                          <TextInput
                            key={i}
                            value={item}
                            size={inputSize}
                            style={{ flex: 1 }}
                            onChange={e => handleArrayChange(e, i)} />
                          <Button color="red" variant="outline" style={{ height: isMobile ? 44 : "100%", minWidth: isMobile ? 44 : undefined, marginLeft: 8 }} onClick={() => handleArrayRemove(i)} size={inputSize} aria-label="Remove item">
                            <Trash size={isMobile ? 18 : 16} />
                          </Button>
                        </div>
                      ))}
                      <Button color="cap-navy" variant="outline" onClick={handleArrayAdd} size={inputSize} style={{ maxWidth: isMobile ? '100%' : '50%', minWidth: isMobile ? '100%' : '25%', minHeight: isMobile ? 44 : undefined, marginTop: isMobile ? 8 : 0 }} leftIcon={<Plus size={16} />}>Add</Button>
                    </>
                  );
                case "DATE":
                  return (
                    <div style={{ display: isMobile ? "flex" : undefined, flexDirection: isMobile ? "column" : undefined, gap: isMobile ? 8 : 0, width: "100%" }}>
                      <DatePicker
                        size={inputSize}
                        value={date}
                        onChange={(d) => handleDateChange(d, null)}
                        style={{ width: "100%" }}
                      />
                      {shouldIncludeTimeForDate && (
                        <>
                          {" "}
                          <TimeInput
                            size={inputSize}
                            icon={<Clock size={18} />}
                            value={time}
                            onChange={(t) => handleDateChange(null, t)}
                            withSeconds={false}
                            style={{ width: "100%", marginTop: isMobile ? 8 : 0 }}
                          />
                        </>
                      )}
                    </div>
                  );
                case "BOOLEAN":
                  return (
                    <Checkbox
                      size={inputSize}
                      color="cap-navy"
                      checked={form.values[fieldKey]}
                      onChange={(v) =>
                        form.setFieldValue(fieldKey, v.currentTarget.checked)
                      }
                    />
                  );
                case "SELECT":
                  return (
                    <Select
                      size={inputSize}
                      searchable
                      allowDeselect
                      style={{ width: "100%" }}
                      data={(selectOptions || []).map((option) => {
                        return {
                          value: option.id,
                          label: selectLabelKey ? String(option[selectLabelKey] || "Unknown") : "Unknown",
                        };
                      })}
                      {...inputProps}
                      onChange={(val) => {
                        inputProps.onChange(val);
                        if (adjacentFields && selectOptions && val) {
                          const selectedOption = selectOptions.find((opt) => opt.id === val);
                          if (selectedOption) {
                            Object.keys(adjacentFields).forEach((adjKey) => {
                              const optionField = adjacentFields[adjKey];
                              form.setFieldValue(adjKey, selectedOption[optionField] ?? null);
                            });
                          }
                        } else if (!val && adjacentFields) {
                          Object.keys(adjacentFields).forEach((adjKey) => {
                            form.setFieldValue(adjKey, null);
                          });
                        }
                      }}
                    />
                  );
                default:
                  return (
                    <Text style={{ wordWrap: "break-word" }}>{value}</Text>
                  );
              }
            })()
            : (() => {
              switch (dataType) {
                case "COORDINATE":
                  return (
                    <a
                      href={`https://www.google.com/maps?q=${value.split(", ")[1]
                        },${[value.split(", ")[0]]}&ll=${value.split(", ")[1]
                        },${[value.split(", ")[0]]}&z=14`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Text style={{ wordWrap: "break-word" }}>{value}</Text>
                    </a>
                  );
                case "BOOLEAN":
                  return (
                    <Text style={{ wordWrap: "break-word" }}>{value ? "TRUE" : "FALSE"}</Text>
                  );
                default:
                  if (dataType === "SELECT" && selectOptions?.length > 0) {
                    const option = selectOptions.find(o => o.id === value);
                    return (
                      <Text style={{ wordWrap: "break-word" }}>
                        {option && selectLabelKey ? option[selectLabelKey] : value}
                      </Text>
                    );
                  }
                  return (
                    <Text style={{ wordWrap: "break-word" }}>{value}</Text>
                  );
              }
            })()}
        </Grid.Col>
      </Grid>
    </>
  );
}
